import { useState, useRef } from 'react'
import { RestaurantMenu, CancelOutlined, Speed, AccountBalanceWallet, AccountBalance, History, MoreVert, CreditCard } from '@mui/icons-material'
import useMediaQuery from '@mui/material/useMediaQuery'
import LoadMealBalance from './LoadMealBalance'
import { Avatar, Box, Button, Chip, Paper, Typography, useTheme, Divider, IconButton, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material'
import CancelCardForm, { CancelCardFormRef } from './CancelCardForm'
import { ConfirmationModal } from '../../../../components/ConfirmationModal'
import { useCardStatusCancelMutation } from '../../services/services'
import { encrypt } from '../../../../utils/encrypt'
import { enqueueSnackbar } from 'notistack'
import { usePermission } from '../../../../hooks/usePermission'
import RefundHistoryModal from './RefundHistoryModal'
import { useSendMealAllowanceListWithWalletNoMutation } from '../../../approvals/services/approvalService'
import UploadByCardNoButton from '../../../../components/mealCardActions/UploadByCardNoButton';
import UploadByAccountNoButton from '../../../../components/mealCardActions/UploadByAccountNoButton';

const MealCardActions = ({ onOpenRefundHistory }: { onOpenRefundHistory?: () => void }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [actionsAnchorEl, setActionsAnchorEl] = useState<null | HTMLElement>(null);
    const actionsMenuOpen = Boolean(actionsAnchorEl);
    const [openByCardNo, setOpenByCardNo] = useState(false);
    const [openByAccountNo, setOpenByAccountNo] = useState(false);
    const [isCancelCardLoading, setIsCancelCardLoading] = useState(false);
    const [openLoadBalanceModal, setOpenLoadBalanceModal] = useState(false);
    const [openCancelCardModal, setOpenCancelCardModal] = useState(false);
    const [showCancelSubmitConfirm, setShowCancelSubmitConfirm] = useState(false);
    const [cancelSubmitConfirmCount, setCancelSubmitConfirmCount] = useState(0);
    const [openRefundHistoryModal, setOpenRefundHistoryModal] = useState(false);

    const handleActionsMenuOpen = (e: React.MouseEvent<HTMLButtonElement>) => setActionsAnchorEl(e.currentTarget);
    const handleActionsMenuClose = () => setActionsAnchorEl(null);
    const cancelCardFormRef = useRef<CancelCardFormRef>(null);
    const [cardStatusCancel] = useCardStatusCancelMutation();
    const [sendMealAllowanceListWithWalletNo] = useSendMealAllowanceListWithWalletNoMutation();
    // Yetki kontrolleri
    const { hasPermission: hasLoadBalancePermission, isLoading: isLoadBalancePermissionLoading } = usePermission({
        code: 'Corporate.POST.Writing.KartBakiyeYükleme'
    })

    const { hasPermission: canViewMealCardList, isLoading: isMealCardListPermissionLoading } = usePermission({
        code: 'Corporate.POST.Reading.KartListeleme(YemekKartı)'
    })

    const { hasPermission: hasCancelCardPermission, isLoading: isCancelCardPermissionLoading } = usePermission({
        code: 'Corporate.POST.Updating.YemekKartıİşlemleri'
    })

    const openNoMealCardListPermissionWarning = () => {
        enqueueSnackbar(
            'Yemek kartı listesi görüntüleme yetkiniz bulunmadığından bu işlem gerçekleştirilememektedir.',
            { variant: 'warning' }
        );
    };

    const handleLoadBalance = () => {
        setOpenLoadBalanceModal(true)
    }

    const handleCancelCard = () => {
        if (!isMealCardListPermissionLoading && !canViewMealCardList) {
            openNoMealCardListPermissionWarning();
            return;
        }
        setOpenCancelCardModal(true)
    }



    const closeCancelCardModal = () => {
        setOpenCancelCardModal(false)
        setShowCancelSubmitConfirm(false)
    }

    const handleRequestCancelSubmit = () => {
        if (!cancelCardFormRef.current) return

        const isValid = cancelCardFormRef.current.validateForm()
        if (!isValid) return

        const formData = cancelCardFormRef.current.getFormData()
        setCancelSubmitConfirmCount(formData.selectedCards.length)
        setShowCancelSubmitConfirm(true)
    }

    const performCancelSubmit = async () => {
        if (!cancelCardFormRef.current) return

        const formData = cancelCardFormRef.current.getFormData()
        setIsCancelCardLoading(true)

        try {
            const requestPayload = {
                cardInfo: formData.selectedCards.map(card => ({
                    cardNo: encrypt(card.cardNo)
                })),
                cancelReason: "",
                cancelReasonDescription: formData.cancelReasonDescription,
                companyId: formData.userData?.companyId || 0
            }

            const result = await cardStatusCancel(requestPayload)
            const response = result as any

            if (response?.data?.success) {
                enqueueSnackbar(response.data.message || 'Kartlar başarıyla iptal edildi', {
                    variant: 'success'
                })
                closeCancelCardModal()
            }
        } catch (error: any) {
            const errorMessage = error?.data?.message || error?.message || 'Kart iptal işlemi sırasında bir hata oluştu. Lütfen tekrar deneyin.'
            console.log(errorMessage);
        } finally {
            setIsCancelCardLoading(false)
        }
    }

    const cancelSubmitConfirmContent =
        cancelSubmitConfirmCount === 1
            ? 'Seçilen kartı iptal etmek istediğinize emin misiniz?'
            : `Seçilen ${cancelSubmitConfirmCount} adet kartı iptal etmek istediğinize emin misiniz?`

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2, sm: 3 },
                mb: 3,
                borderRadius: 3,
                background: 'rgba(255,255,255,0.7)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(226, 232, 240, 0.8)',
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{
                        background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                        mr: 2,
                        width: 40,
                        height: 40
                    }}>
                        <RestaurantMenu />
                    </Avatar>
                    <Box>
                        <Typography variant="h5" sx={{
                            color: '#1a202c',
                            fontWeight: 700,
                            mb: 0.5,
                            fontSize: '18px !important'
                        }}>
                            Yemek Kartı İşlemleri
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b' }}>
                            Bakiye yükleme ve kart iptal işlemlerinizi gerçekleştirin
                        </Typography>
                    </Box>
                </Box>
                {!isMobile && (
                    <Chip
                        icon={<Speed />}
                        label="Hızlı İşlem"
                        sx={{
                            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                            color: 'white',
                            fontWeight: 600,
                            px: 2,
                            '& .MuiChip-icon': { color: 'white' }
                        }}
                    />
                )}
            </Box>

            {/* Action Buttons - Kart İşlemleri layout */}
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                {isMobile ? (
                    <>
                        <Button
                            variant="outlined"
                            startIcon={<MoreVert />}
                            onClick={handleActionsMenuOpen}
                            sx={{
                                border: '1px solid #d1d5db',
                                borderRadius: 2,
                                px: 2,
                                py: 1.2,
                                color: '#64748b',
                                fontWeight: 600,
                                fontSize: '0.875rem',
                                textTransform: 'none',
                                '&:hover': {
                                    background: 'rgba(30, 60, 114, 0.05)',
                                    borderColor: '#1e3c72',
                                    color: '#1e3c72'
                                }
                            }}
                        >
                            İşlemler
                        </Button>
                        <Menu
                            anchorEl={actionsAnchorEl}
                            open={actionsMenuOpen}
                            onClose={handleActionsMenuClose}
                            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                            PaperProps={{
                                sx: { mt: 1.5, minWidth: 240, borderRadius: 2, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }
                            }}
                        >
                            <MenuItem onClick={() => { handleActionsMenuClose(); setOpenByCardNo(true); }}>
                                <ListItemIcon sx={{ minWidth: 36 }}><CreditCard /></ListItemIcon>
                                <ListItemText primary="Kart No ile Bakiye Yükle" primaryTypographyProps={{ fontWeight: 600, fontSize: '0.875rem' }} />
                            </MenuItem>
                            <MenuItem onClick={() => { handleActionsMenuClose(); setOpenByAccountNo(true); }}>
                                <ListItemIcon sx={{ minWidth: 36 }}><AccountBalance /></ListItemIcon>
                                <ListItemText primary="Hesap No ile Bakiye Yükle" primaryTypographyProps={{ fontWeight: 600, fontSize: '0.875rem' }} />
                            </MenuItem>
                            {/* Bakiye Yükle artık kullanılmıyor
                            {hasLoadBalancePermission && (
                                <MenuItem onClick={() => { handleActionsMenuClose(); handleLoadBalance(); }}>
                                    <ListItemIcon sx={{ minWidth: 36 }}><AccountBalanceWallet /></ListItemIcon>
                                    <ListItemText primary={isLoadBalancePermissionLoading ? 'Yükleniyor...' : 'Bakiye Yükle'} primaryTypographyProps={{ fontWeight: 600, fontSize: '0.875rem' }} />
                                </MenuItem>
                            )}
                            */}
                            {hasCancelCardPermission && (
                                <MenuItem onClick={() => { handleActionsMenuClose(); handleCancelCard(); }} disabled={isCancelCardLoading || isCancelCardPermissionLoading}>
                                    <ListItemIcon sx={{ minWidth: 36 }}><CancelOutlined /></ListItemIcon>
                                    <ListItemText primary={isCancelCardLoading ? 'İptal Ediliyor...' : isCancelCardPermissionLoading ? 'Yükleniyor...' : 'Kartı İptal Et'} primaryTypographyProps={{ fontWeight: 600, fontSize: '0.875rem' }} />
                                </MenuItem>
                            )}
                            <MenuItem onClick={() => { handleActionsMenuClose(); setOpenRefundHistoryModal(true); }}>
                                <ListItemIcon sx={{ minWidth: 36 }}><History /></ListItemIcon>
                                <ListItemText primary="İade Geçmişi" primaryTypographyProps={{ fontWeight: 600, fontSize: '0.875rem' }} />
                            </MenuItem>
                        </Menu>
                    </>
                ) : (
                    <>
                        <UploadByCardNoButton />
                        <Divider orientation="vertical" flexItem sx={{ height: 40, bgcolor: '#cbd5e1', mx: 1 }} />
                        <UploadByAccountNoButton />
                        {/* Bakiye Yükle artık kullanılmıyor
                        {hasLoadBalancePermission && (
                            <Button
                                variant="outlined"
                                startIcon={<AccountBalanceWallet />}
                                onClick={handleLoadBalance}
                                disabled={isLoadBalancePermissionLoading}
                                sx={{
                                    border: '1px solid', borderColor: '#10b981', color: '#059669',
                                    fontWeight: 600, fontSize: '0.875rem', textTransform: 'none', borderRadius: 2,
                                    px: 3, py: 1.2, flexShrink: 0,
                                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.15)', background: 'transparent',
                                    '&:hover': { background: 'rgba(16,185,129,0.08)', borderColor: '#059669', boxShadow: '0 6px 20px rgba(16, 185, 129, 0.18)', transform: 'translateY(-1px)' },
                                    '&:disabled': { borderColor: 'rgba(16, 185, 129, 0.3)', color: 'rgba(5, 150, 105, 0.5)', background: 'rgba(255, 255, 255, 0.5)' },
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                {isLoadBalancePermissionLoading ? 'Yükleniyor...' : 'Bakiye Yükle'}
                            </Button>
                        )}
                        */}
                        {hasCancelCardPermission && (
                            <Button
                                variant="outlined"
                                startIcon={<CancelOutlined />}
                                onClick={handleCancelCard}
                                disabled={isCancelCardLoading || isCancelCardPermissionLoading}
                                sx={{
                                    textTransform: 'none', fontWeight: 600, fontSize: '0.875rem', borderRadius: 2,
                                    px: 3, py: 1.2, flexShrink: 0,
                                    border: '1px solid', borderColor: '#f59e0b', color: '#d97706',
                                    background: 'rgba(255,255,255,0.8)', backdropFilter: 'blur(10px)',
                                    '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', background: 'rgba(245, 158, 11, 0.1)' },
                                    '&:disabled': { borderColor: 'rgba(245, 158, 11, 0.3)', color: 'rgba(217, 119, 6, 0.5)', background: 'rgba(255, 255, 255, 0.5)' },
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                {isCancelCardLoading ? 'İptal Ediliyor...' : isCancelCardPermissionLoading ? 'Yükleniyor...' : 'Kartı İptal Et'}
                            </Button>
                        )}
                        <Button
                            variant="outlined"
                            sx={{
                                border: '1px solid', borderColor: '#6366f1', color: '#3730a3',
                                fontWeight: 600, fontSize: '0.875rem', textTransform: 'none', borderRadius: 2,
                                px: 3, py: 1.2, flexShrink: 0,
                                boxShadow: '0 4px 12px rgba(99, 102, 241, 0.12)', background: 'transparent',
                                '&:hover': { background: 'rgba(99,102,241,0.08)', borderColor: '#3730a3', boxShadow: '0 6px 20px rgba(99,102,241,0.18)', transform: 'translateY(-1px)' },
                                transition: 'all 0.3s ease'
                            }}
                            onClick={() => setOpenRefundHistoryModal(true)}
                        >
                            İade Geçmişi
                        </Button>
                    </>
                )}
            </Box>

            {/* Mobile: controlled modals (opened from dropdown) */}
            {isMobile && (
                <>
                    <UploadByCardNoButton open={openByCardNo} onOpenChange={setOpenByCardNo} renderButton={false} />
                    <UploadByAccountNoButton open={openByAccountNo} onOpenChange={setOpenByAccountNo} renderButton={false} />
                </>
            )}

            <ConfirmationModal
                isOpen={openRefundHistoryModal}
                onClose={() => setOpenRefundHistoryModal(false)}
                title="İade Geçmişi"
                isLoading={false}
                confirmButtonText=""
                optionalWidth="1400px"
                closeButtonText=""
                customHeaderStyle={{}}
            >
                <RefundHistoryModal />
            </ConfirmationModal>

            {/* Load Balance Modal */}
            <ConfirmationModal
                isOpen={openLoadBalanceModal}
                onClose={() => setOpenLoadBalanceModal(false)}
                onConfirm={() => { }} // Boş fonksiyon, çünkü LoadMealBalance kendi confirm'ini yönetiyor
                title="Bakiye Yükle - Toplu İşlem"
                isLoading={false}
                confirmButtonText="" // Boş string ile buton gizle
                closeButtonText="" // Boş string ile buton gizle
                optionalWidth="1200px"
                customHeaderStyle={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    titleColor: 'white',
                    icon: (
                        <Avatar sx={{
                            background: 'rgba(255,255,255,0.2)',
                            color: 'white',
                            width: 32,
                            height: 32
                        }}>
                            <AccountBalanceWallet fontSize="small" />
                        </Avatar>
                    )
                }}
            >
                <LoadMealBalance onClose={() => setOpenLoadBalanceModal(false)} />
            </ConfirmationModal>

            {/* Cancel Card Modal */}
            <ConfirmationModal
                isOpen={openCancelCardModal}
                onClose={closeCancelCardModal}
                onConfirm={handleRequestCancelSubmit}
                title="Kart İptal İşlemi"
                isLoading={false}
                confirmButtonText="İptal Et"
                closeButtonText="Vazgeç"
                optionalWidth="500px"
                customHeaderStyle={{
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    titleColor: 'white',
                    icon: (
                        <Avatar sx={{
                            background: 'rgba(255,255,255,0.2)',
                            color: 'white',
                            width: 32,
                            height: 32
                        }}>
                            <CancelOutlined fontSize="small" />
                        </Avatar>
                    )
                }}
            >
                <CancelCardForm
                    ref={cancelCardFormRef}
                    onClose={closeCancelCardModal}
                    isOpen={openCancelCardModal}
                />
            </ConfirmationModal>

            <ConfirmationModal
                isOpen={showCancelSubmitConfirm}
                onClose={() => setShowCancelSubmitConfirm(false)}
                onConfirm={() => void performCancelSubmit()}
                title="İşlem Onayı"
                content={cancelSubmitConfirmContent}
                confirmButtonText={isCancelCardLoading ? 'İptal Ediliyor...' : 'Onayla'}
                closeButtonText="Vazgeç"
                isLoading={isCancelCardLoading}
                optionalWidth="auto"
            />
        </Paper>
    )
}

export default MealCardActions
